import { FC, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from 'chart.js';
import moment from 'moment';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import { TotalProductionData } from 'hooks/useTotalProduction';
import { sortBy } from 'lodash';
import PeriodComparison from 'pages/OnlineSchedulingReportPage/PeriodComparison/PeriodComparison';
import { formatUsdCurrency } from 'utils/formatUsdCurrency';
import {
  LABEL_FORMAT_BY_GRANULARITY,
  bucketTimeSeries,
  getGranularityForRange,
} from 'utils/bucketTimeSeries';
import { DATE_TIME_FORMAT, numberOfDatesBetween } from 'utils/datetime';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

interface TotalProductionChartProps {
  selectedDateRange: { fromDate: string; toDate: string };
  hourType: string;
  totalProductionData?: TotalProductionData | null;
  isLoading: boolean;
}

const TotalProductionChart: FC<TotalProductionChartProps> = ({
  selectedDateRange,
  hourType,
  totalProductionData: totalProduction,
  isLoading,
}) => {
  const { chartData, bucketed, granularity, averageProduction } =
    useMemo(() => {
      const dailyDetails = totalProduction?.periodData?.details
        ? sortBy(totalProduction.periodData.details, 'date')
        : [];
      const resolvedGranularity = getGranularityForRange(
        selectedDateRange.fromDate,
        selectedDateRange.toDate
      );
      const bucketedEntries = bucketTimeSeries(
        dailyDetails,
        resolvedGranularity,
        ['totalProduction']
      );
      const labelFormat = LABEL_FORMAT_BY_GRANULARITY[resolvedGranularity];

      let numberOfDates = 1;

      if (
        totalProduction?.periodData?.period.startDate &&
        totalProduction?.periodData?.period.endDate
      ) {
        numberOfDates = numberOfDatesBetween(
          {
            value: totalProduction?.periodData?.period.startDate,
            format: DATE_TIME_FORMAT.DEFAULT,
          },
          {
            value: totalProduction?.periodData?.period.endDate,
            format: DATE_TIME_FORMAT.DEFAULT,
          }
        );
      }

      return {
        bucketed: bucketedEntries,
        granularity: resolvedGranularity,
        averageProduction: dailyDetails.length
          ? Math.round(
              dailyDetails.reduce((sum, d) => sum + d.totalProduction, 0) /
                numberOfDates
            )
          : 0,
        chartData: {
          labels: bucketedEntries.map((d) =>
            moment(d.date).format(labelFormat)
          ),
          datasets: [
            {
              fill: true,
              data: bucketedEntries.map((d) => d.totalProduction),
              borderColor: '#B01BAE',
              backgroundColor: '#f9eaf5',
              tension: 0,
              pointBackgroundColor: '#B01BAE',
            },
          ],
        },
      };
    }, [totalProduction, selectedDateRange.fromDate, selectedDateRange.toDate]);

  const currentTotal = totalProduction?.periodData?.totalProduction ?? 0;
  const lastTotal = totalProduction?.lastPeriodData?.totalProduction ?? 0;

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner className="all-child:fill-magenta w-4 h-4" />
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center">
        <div className="flex gap-x-0.6 font-bold items-baseline">
          <span className="text-18 leading-[2.7rem]">$</span>
          <span className="text-30 text-magenta">
            {formatUsdCurrency(totalProduction?.periodData.totalProduction, {
              includeSymbol: false,
            })}
          </span>
        </div>
        <div className="flex items-center gap-x-1.6 text-14 text-darkest-grey">
          <span>
            <span className="font-bold">
              {formatUsdCurrency(averageProduction)}
            </span>{' '}
            avg / day
          </span>
          <PeriodComparison current={currentTotal} last={lastTotal} />
        </div>
      </div>

      <div className="pr-2.3">
        <Line
          data={chartData}
          className="max-h-[30rem]"
          options={{
            plugins: {
              title: { display: false },
              subtitle: { display: false },
              legend: { display: false },
              tooltip: {
                callbacks: {
                  title: (items) => {
                    const index = items[0]?.dataIndex ?? 0;
                    const entry = bucketed[index];
                    if (granularity === 'day' && entry) {
                      return moment(entry.date).format('YYYY/MM/DD');
                    }
                    return items[0]?.label ?? '';
                  },
                  label: (ctx) => formatUsdCurrency(ctx.parsed.y),
                },
              },
            },
          }}
        />
      </div>
    </>
  );
};

export default TotalProductionChart;
